import { produce } from 'immer';
import { isSDKLite } from 'common/browser';
import createCorrelationId from 'communicator/utils/correlation-id';
import createSessionId from 'communicator/utils/session-id';
import { sessionStorage } from 'communicator/utils/storage';
import { generateUuid } from 'communicator/utils/uuid';
import { TNCEntryInfo } from 'types/api/b2ciam';
import { CardListMaskedAddress, CardListMaskedCard } from 'types/api/card-list';
import { EnrollCardResponse } from 'types/api/enroll-card';
import { Address, PaymentInstrument, PurchaseIntentResponse } from 'types/api/intent';
import {
  CardNetwork,
  CountryCode,
  FeatureToggles,
  LookupStatus,
  TNCStatus,
  ToRoute,
  UserType
} from 'types/data';
import { DCFActionCode, IdentityType, MobileNumber } from 'types/emvco';
import { FetchResponseValue } from 'types/fetch';
import { CheckoutData, CheckoutRequestMetadata } from 'types/visa-sdk/checkout';
import { InitData } from 'types/visa-sdk/init';

const visitId = sessionStorage.getItem('visitId') ?? `VID_${generateUuid()}`;
sessionStorage.setItem('visitId', visitId);

export type UserProfile = {
  accessToken?: {
    /**
     * When converting a federated idToken to an accessToken, the backend
     * returns an auth_level field indicating the type of stepup the user
     * performed by the third-party SRC system. The values are either:
     *
     * 1. sms_otp/email_otp - The user has already performed a step up and does
     *    not need to perform OTP again.
     * 2. cookie/swk - The user was recognized by cookies, and may need to be
     *    stepped up.
     *
     * The value is relevant when performing user account changes in
     * non-integrated flows. E.g. updating an address from the review screen.
     */
    authLevel?: 'aal0' | 'aal1';
    expiresIn: number;
    token: string;
    isPartial: boolean;
  } | null;
  country?: CountryCode;
  email?: string;
  emailHash?: string;
  flowType?: UserType;
  identityType?: IdentityType;
  idToken?: string;
  idValidationSessionId?: string;
  isComplianceTncPassed?: boolean;
  isLocked?: boolean;
  isLoggedInOtpUser?: boolean;
  isNonVisaRecognizedUser?: boolean;
  isPrivacyPolicyPresent?: boolean;
  isRecognizedUser?: boolean;
  locale?: string;
  lookupStatus?: LookupStatus;
  mobileNumber?: MobileNumber;
  phone?: string;
  tnc?: {
    status?: TNCStatus | null;
    tncEntryInfo?: TNCEntryInfo;
  };
  xAuthCode?: string;
};

export type FlowState = {
  addCardInfo?: {
    cardSuffix: string;
    enrollCardResponse: EnrollCardResponse;
    networkId: CardNetwork;
    panJWE: string;
  };
  dcfActionCode?: DCFActionCode;
  grant?: boolean;
  toRoute?: ToRoute;
  why?: string;
};

type ServerState = {
  expiresIn: number;
  token: string;
};

/* START GENAI@GHCOPILOT */
export type CommunicatorState = {
  config: {
    checkout?: CheckoutData;
    init?: InitData;
  };
  featureToggles: FeatureToggles;
  flow: FlowState;
  meta: {
    cardIdMapping: Record<string, string | undefined>;
    checkoutRequestMetadata?: CheckoutRequestMetadata;
    correlationId: string;
    init: boolean;
    integrationType: 'psp' | 'visa-srci' | 'vsb-buttonless' | 'vsb';
    isBiometricsEnabledDevice: boolean;
    isCheckoutDataReady: boolean;
    isSPCSupported: boolean;
    isSplunkLoggingEnabled: boolean;
    namespace: 'vdcp-mobile-web' | 'vdcp-web';
    orgId: string;
    otpProvider?: string;
    selectedCardId: string;
    serverState: ServerState | null;
    sessionId: string;
    split: boolean;
    thmProfileHint: string | null;
    threeDsUrl: string | null;
    isVdiDisabled: boolean;
    vIntentId?: string;
    visitId: string;
    vppsOrigin: string;
  };
  profile: {
    maskedCards?: CardListMaskedCard[];
    maskedShippingAddresses?: CardListMaskedAddress[];
    paymentInstrument?: PaymentInstrument[];
    shippingAddress?: Address[];
  };
  putIntentResponse: FetchResponseValue<PurchaseIntentResponse> | null;
  user: UserProfile | null;
  proofState: {
    codeChallenge: string | null;
    codeVerifier: string | null;
  };
};

let state: CommunicatorState = {
  config: {},
  featureToggles: { ...window.VISA_GLOBALS.enabledFeatureToggles },
  flow: {},
  meta: {
    cardIdMapping: {},
    // default correlationId is overwritten in init()
    correlationId: createCorrelationId(),
    init: false,
    integrationType: 'psp',
    isBiometricsEnabledDevice: false,
    isCheckoutDataReady: false,
    isSPCSupported: false,
    isSplunkLoggingEnabled: false,
    isVdiDisabled: false,
    namespace: isSDKLite() ? 'vdcp-mobile-web' : 'vdcp-web',
    orgId: window.VISA_GLOBALS.orgId,
    selectedCardId: '',
    serverState: null,
    sessionId: createSessionId(
      window.VISA_GLOBALS.enabledFeatureToggles.enableVpps,
      window.VISA_GLOBALS.enabledFeatureToggles.useSessionId32Char
    ),
    split: false,
    thmProfileHint: null,
    threeDsUrl: null,
    visitId,
    vppsOrigin: window.VISA_GLOBALS.vppsOrigin
  },
  profile: {},
  proofState: {
    codeChallenge: null,
    codeVerifier: null
  },
  putIntentResponse: null,
  user: null
};
/* END GENAI@GHCOPILOT */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let devTools: any = null;

if (process.env.NODE_ENV !== 'production') {
  devTools = window.__REDUX_DEVTOOLS_EXTENSION__?.connect({
    name: `Visa SRC SDK (${window.location.origin})`
  });

  devTools?.init(state);
}

const subscribers: (() => unknown)[] = [];

export function getState() {
  return state;
}

type ChangeHandler<StatePartial> = (
  currentState: StatePartial,
  previousState: StatePartial
) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function subscribe<S extends (s: CommunicatorState) => any>(
  selector: S,
  onChange: ChangeHandler<ReturnType<S>>
) {
  let currentState = selector(getState());

  const subscriber = () => {
    const nextState = selector(getState());

    if (currentState !== nextState) {
      const previousState = currentState;
      currentState = nextState;
      onChange(currentState, previousState);
    }
  };

  subscribers.push(subscriber);

  return () => {
    const index = subscribers.indexOf(subscriber);
    subscribers.splice(index, 1);
  };
}

function notifySubscribers() {
  subscribers.forEach(subscriber => subscriber());
}

function produceAndNotify(type: string, recipe: (state: CommunicatorState) => void) {
  state = produce(state, recipe);
  notifySubscribers();

  if (process.env.NODE_ENV !== 'production') {
    devTools?.send(type, state);
  }
}

export function reset() {
  produceAndNotify('reset', draft => {
    draft.flow = {};
    draft.user = null;
    draft.meta = {
      ...draft.meta,
      cardIdMapping: {},
      correlationId: createCorrelationId(),
      isCheckoutDataReady: false,
      split: false,
      vIntentId: undefined
    };

    draft.profile = {};
    draft.putIntentResponse = null;
  });
}

export function resetUserProfile() {
  produceAndNotify('resetUserProfile', draft => {
    draft.user = null;
  });
}

export function saveCheckoutData(data: CheckoutData) {
  produceAndNotify('saveCheckoutData', draft => {
    draft.config.checkout = data;
  });
}

export function saveFlow(flow: Partial<CommunicatorState['flow']>) {
  produceAndNotify('saveFlow', draft => {
    draft.flow = { ...state.flow, ...flow };
  });
}

export function saveInitData(data: InitData) {
  produceAndNotify('saveInitData', draft => {
    draft.config.init = data;
  });
}

export function saveMeta(meta: Partial<CommunicatorState['meta']>) {
  produceAndNotify('saveMeta', draft => {
    draft.meta = { ...state.meta, ...meta };
  });
}

export function saveProfile(profile: Partial<CommunicatorState['profile']>) {
  produceAndNotify('saveProfile', draft => {
    draft.profile = { ...state.profile, ...profile };
  });
}

/* START GENAI@GHCOPILOT */

export function saveProofState(proof: Partial<CommunicatorState['proofState']>) {
  produceAndNotify('proofState', draft => {
    draft.proofState = { ...state.proofState, ...proof };
  });
}
/* END GENAI@GHCOPILOT */

export function savePutIntentResponse(data: FetchResponseValue<PurchaseIntentResponse>) {
  produceAndNotify('savePutIntentResponse', draft => {
    draft.putIntentResponse = data;
  });
}

export function saveUser(user: UserProfile) {
  produceAndNotify('saveUser', draft => {
    draft.user = { ...state.user, ...user };
  });
}

export function saveCheckoutRequestMetadata(metadata: CheckoutRequestMetadata) {
  produceAndNotify('saveCheckoutRequestMetadata', draft => {
    draft.meta = {
      ...draft.meta,
      checkoutRequestMetadata: metadata
    };
  });
}
